import React from "react";
import { Link } from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";

import { OrganizationAggregate } from "../../../dtos/organizationAggregate";
import { ActiveStatus } from "../../../enums/ActiveStatus";
import { useAuth } from "../../../features/auth/context";
import { ResponsiveDataView, Column } from "../../shared/ResponsiveDataView";

import { OrgRowActions } from "./OrgRowActions";

interface Props {
    orgs: OrganizationAggregate[];
    orgsAreLoading: boolean;
    dealerId: number | undefined;
    onDeleteOrg: (id: number) => void;
    onLoadOrgs: () => void;
}

const countLink = (dealerId: number | undefined, orgId: number, resource: string, count: number) => {
    if (count <= 0) return <>{count}</>;
    const base = dealerId ? `/dealers/${dealerId}/organizations/${orgId}` : `/organizations/${orgId}`;
    return (
        <Link component={RouterLink} to={`${base}/${resource}`}>
            {count}
        </Link>
    );
};

export function OrgsTable({
    orgs,
    orgsAreLoading,
    dealerId,
    onDeleteOrg,
    onLoadOrgs,
}: Props) {
    const { isReadOnlyUser } = useAuth();

    const columns: Column<OrganizationAggregate>[] = [
        { key: 'id', label: 'Id' },
        { key: 'name', label: 'Name', primary: true },
        { key: 'dealerName', label: 'Dealer', hideWhenEmpty: true },
        { key: 'numberOfSites', label: '# of Sites', render: (o) => countLink(dealerId, o.id, 'sites', o.numberOfSites) },
        { key: 'numberOfCustomers', label: '# of Customers', render: (o) => countLink(dealerId, o.id, 'customers', o.numberOfCustomers) },
        { key: 'numberOfEmployees', label: '# of Employees', render: (o) => countLink(dealerId, o.id, 'employees', o.numberOfEmployees) },
    ];

    const rowActions = !isReadOnlyUser
        ? (o: OrganizationAggregate) => (
            <OrgRowActions
                org={o}
                dealerId={dealerId}
                orgsAreLoading={orgsAreLoading}
                onDeleteOrg={onDeleteOrg}
                onLoadOrgs={onLoadOrgs}
            />
        )
        : undefined;

    return (
        <ResponsiveDataView
            rows={orgs}
            columns={columns}
            rowKey={(o) => o.id}
            actions={rowActions}
            isInactive={(o) => o.activeStatus !== undefined && o.activeStatus !== ActiveStatus.Active}
            loading={orgsAreLoading}
        />
    );
}
